// DestinationCard.tsx — Hover-parallax photo card for TrendingGrid.
// Responsive: 4-col desktop, 2-col mobile.

import { useState } from 'react';
import { notYetWiredToast } from '@/components/primitives';
import type { Destination } from '@/lib/destinations-mock';

const F = '"Inter Variable","Inter",system-ui,sans-serif';
const DISPLAY = '"Fraunces Variable","Fraunces",Georgia,serif';

interface DestinationCardProps {
  destination: Destination;
}

export function DestinationCard({ destination: d }: DestinationCardProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={() => notYetWiredToast('Flight search')}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && notYetWiredToast('Flight search')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 24,
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        background: '#131C30',
        border: '1px solid rgba(255,255,255,0.07)',
        transition: 'transform 0.28s cubic-bezier(.2,.9,.3,1.2), box-shadow 0.28s ease',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        boxShadow: hovered
          ? '0 24px 48px rgba(0,0,0,0.35)'
          : '0 8px 24px rgba(0,0,0,0.15)',
      }}
    >
      {/* Photo */}
      <div style={{ height: 240, overflow: 'hidden', position: 'relative' }}>
        <img
          src={d.img}
          alt={d.city}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.6s ease',
            transform: hovered ? 'scale(1.04)' : 'scale(1)',
          }}
        />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.7) 100%)',
        }}/>
        {/* Tag */}
        <div style={{ position: 'absolute', top: 12, left: 12 }}>
          <span style={{
            padding: '4px 10px', borderRadius: 999,
            background: '#4B8EDB', color: '#fff',
            fontFamily: F, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
          }}>● {d.tag}</span>
        </div>
        {/* City overlay */}
        <div style={{ position: 'absolute', bottom: 14, left: 16, right: 16, color: '#fff' }}>
          <div style={{ fontFamily: F, fontSize: 11, letterSpacing: 2, opacity: 0.85, fontWeight: 600 }}>
            {d.country.toUpperCase()} · {d.code}
          </div>
          <h3 style={{
            fontFamily: DISPLAY,
            fontSize: 32, fontWeight: 500, margin: '4px 0 0',
            lineHeight: 0.95, letterSpacing: -0.02,
          }}>{d.city}</h3>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontFamily: F, fontSize: 10, color: '#8C98B0', letterSpacing: 1, fontWeight: 600, textTransform: 'uppercase' }}>from</div>
          <div style={{ fontFamily: F, fontSize: 20, fontWeight: 700, color: '#F4F3F0', marginTop: 2 }}>
            ₹{d.price.toLocaleString('en-IN')}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 14 }}>☀</span>
          <span style={{ fontFamily: F, fontSize: 13, color: '#8C98B0', fontWeight: 500 }}>{d.weather}</span>
        </div>
      </div>
    </div>
  );
}
